// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionConfig.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import constants from 'logic/data/Izenda.ConnectionConstants';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Header from './Header/Izenda.Header';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import ConnectionDetail from './ConnectionDetail/Izenda.ConnectionDetail';
import DataSourceTreeContainer from './DataSourceTree/Izenda.DataSourceTreeContainer';
import commonMessages from 'share/messages/Izenda.CommonMessages';
import connect from 'share/enhancers/Izenda.Connect';
import { isEmpty, getOr, isNil } from 'lodash/fp';

const MESSAGE_TYPE = commonMessages.MESSAGE_TYPE;

/**
 * @module ConnectionConfig component
 * @example
 *   <ConnectionConfig>
 *     <Header></Header>
 *     <ConnectionDetail>
 *       <ConnectionBuilderModal></ConnectionBuilderModal>
 *     </ConnectionDetail>
 *     <ConnectButton></ConnectButton>
 *     <DataSourceTree></DataSourceTree>
 *   </ConnectionConfig>
 */
@connect()
export default class ConnectionConfig extends Component {
  static propTypes = {
    connectionDetailStore: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    dbTypeList: PropTypes.object.isRequired,
    onSaveCallback: PropTypes.func,
    testConnectionFn: PropTypes.func.isRequired, // function which runs test connection API
    connectToDataSourceFn: PropTypes.func.isRequired, // function which runs connect API
  };

  componentDidMount() {
    const { props } = this;
    props.connectionDetailStore.init();
  }

  componentWillUnmount() {
    const { props } = this;
    props.connectionDetailStore.masterStore.deleteTempExcelFiles();
    props.connectionDetailStore.destroy();
  }

  componentWillReceiveProps(nextProps) {
    const { props } = this;
    if (props.item !== nextProps.item) {
      props.validation.reset();
      props.item.setAlertMessage(commonMessages.EMPTY_MESSAGE);
    }
  }

  /**
   * Retrieve connection detail and update via API.
   */
  onSave = () => {
    const { props } = this;
    const { connectionDetailStore, loader, validation, item, localization, confirmFn, confirmMessage } = props;
    validation
      // Validate connector name field
      .validate('name')
      .then(success => {
        if (!success)
          return;
        // If connector name is ok - continue
        this
          .onTestConnection()
          .then(validateSchemaStatus => {

            item.setAlertMessage(commonMessages.EMPTY_MESSAGE);
            if (!item.canSave) {
              // "No changes found" check
              item.setAlertMessage({
                type: MESSAGE_TYPE.INFO,
                messages: commonMessages.SUPP_MSG_028
              });
              return;
            }

            if (item.canSave && validateSchemaStatus) {
              // If everything is okay: confirm and save
              return confirmFn(
                'Confirmation',
                {
                  description: item.needConfirmMessageForAvailableDatasouceChange
                    ? constants.M05
                    : localization.getText('COMMON_MESSAGES_CONFIRM_SAVE')
                })
                .then(() => {
                  loader.startLoading();
                  connectionDetailStore
                    .save()
                    .then(result => {
                      if (!result.success) {
                        const errorMessage = getOr(null, 'messages[0].messages[0]', result);
                        if (!isNil(errorMessage)) {
                          confirmMessage('error', errorMessage);
                        }
                      }
                      return null;
                    })
                    .finally(() => loader.endLoading());
                  return null;
                });

            } else if (!validateSchemaStatus) {
              // Connection no valid message
              confirmMessage('error', constants.MISMATCH_CONNECIONSTRING_DATASOURCE);
            }
            return null;
          })
          .then(saveErrors => {
            if (!isEmpty(saveErrors)) {
              // if save errors:
              validation.updateErrors(saveErrors);
            } else {
              props.onSaveCallback && props.onSaveCallback();
            }
            return null;
          });
      });
  };

  /**
   * Cancel changes that user made for connection.
   */
  onCancel = () => {
    const { props } = this;
    const { connectionDetailStore, validation, localization } = props;
    const { item } = connectionDetailStore;

    if (item.isDirty) {
      props.confirmFn('Confirmation', {
        description: localization.getText('COMMON_MESSAGES_CONFIRM_CHANGES_LOST')
      }).then(() => {
        connectionDetailStore.masterStore.deleteTempExcelFiles();
        validation.reset();
        return connectionDetailStore.load(item.id);
      });
    } else {
      // No changes found
      item.setAlertMessage({
        type: MESSAGE_TYPE.INFO,
        messages: commonMessages.SUPP_MSG_028
      });
    }
  };

  /**
   * Test input connection string associated with database server type.
   */
  onTestConnection = () => {
    const { connectionDetailStore, testConnectionFn } = this.props;
    return testConnectionFn(connectionDetailStore);
  };

  /**
   * Connect to data source.
   */
  onConnectToDataSource = () => {
    const { connectionDetailStore, connectToDataSourceFn } = this.props;
    return connectToDataSourceFn(connectionDetailStore);
  };

  resetAlertMessage = () => {
    const { props: { connectionDetailStore: { item } } } = this;
    item.setAlertMessage(commonMessages.EMPTY_MESSAGE);
  };

  render() {
    const { props } = this;
    const { connectionDetailStore, item } = props;
    const disabled = !item.isConnectionStringReady;

    return (
      <div className='izenda-TableLayout-Cell'>
        <section className='izenda-Wrapper'>
          <Header
            ref='Header'
            onCancel={this.onCancel}
            onSave={this.onSave}
            disabled={disabled}
            alertMessage={item.alertMessage}
            onResetAlert={this.resetAlertMessage}
          />

          <div className='izenda-Wrapper-content'>
            <ScrollBox>
              <div className='u_fullHeight u_paddingLeftRight'>

                <ConnectionDetail
                  {...props}
                  onTestConnection={this.onTestConnection}
                  onConnectToDataSource={this.onConnectToDataSource}
                />

                <DataSourceTreeContainer
                  store={connectionDetailStore}
                  disabled={disabled}
                  item={item}
                  isAddNew={isEmpty(props.item.id)}
                />
              </div>
            </ScrollBox>
          </div>
        </section>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Connection/ConnectionConfig/Izenda.ConnectionConfig.js